import { useCallback, useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';
import { notificationCountState } from '../atoms';

const useBrowserTabNotification = (title = 'Popp') => {
  const [notificationCount, setNotificationCount] = useRecoilState(
    notificationCountState
  );
  const [favicon, setFavicon] = useState<string | null>(null);
  const [notificationFavicon, setNotificationFavicon] = useState<string | null>(
    null
  );

  useEffect(() => {
    createFavicon();
  }, []);

  const createFavicon = () => {
    const faviconElement = document.querySelector(
      "link[rel~='icon']"
    ) as HTMLLinkElement;

    if (!faviconElement?.href) return;

    setFavicon(faviconElement.href);

    const img = document.createElement('img');
    img.src = faviconElement.href;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const context = canvas.getContext('2d');

      if (!context) return;

      context.drawImage(img, 0, 0, img.width, img.height);
      context.beginPath();
      context.arc(
        img.width - img.width / 5,
        img.height / 5,
        img.width / 5,
        0,
        2 * Math.PI
      );
      context.fillStyle = '#f00000';
      context.fill();
      setNotificationFavicon(canvas.toDataURL('image/png'));
    };
  };

  const addNotification = useCallback(() => {
    setNotificationCount((prevCount) => {
      const newCount = prevCount + 1;
      document.title = `(${newCount}) ${title}`;

      const faviconElement = document.querySelector(
        "link[rel~='icon']"
      ) as HTMLLinkElement;

      if (!faviconElement?.href || !notificationFavicon) return newCount;

      faviconElement.href = notificationFavicon;
      return newCount;
    });
  }, [notificationFavicon, title, setNotificationCount]);

  const clearNotification = useCallback(() => {
    setNotificationCount(0);
    document.title = title;

    const faviconElement = document.querySelector(
      "link[rel~='icon']"
    ) as HTMLLinkElement;

    if (faviconElement && favicon) {
      faviconElement.href = favicon;
    }
  }, [favicon, title, setNotificationCount]);

  const removeNotification = useCallback(() => {
    setNotificationCount((prevCount) => {
      const newCount = prevCount - 1;
      document.title = `(${newCount}) ${title}`;

      const faviconElement = document.querySelector(
        "link[rel~='icon']"
      ) as HTMLLinkElement;

      if (!faviconElement?.href || !notificationFavicon || !favicon)
        return newCount;

      if (newCount <= 0) {
        document.title = title;
        faviconElement.href = favicon;
        return 0;
      }

      return newCount;
    });
  }, [setNotificationCount, title, notificationFavicon, favicon]);

  const setNotificationTo = useCallback(
    (count: number) => {
      const faviconElement = document.querySelector(
        "link[rel~='icon']"
      ) as HTMLLinkElement;

      if (!faviconElement?.href || !notificationFavicon || !favicon) return;

      if (count <= 0) {
        faviconElement.href = favicon;
        return;
      }

      setNotificationCount(count);
      document.title = `(${count}) ${title}`;

      faviconElement.href = notificationFavicon;
    },
    [setNotificationCount, title, notificationFavicon, favicon]
  );

  return {
    notificationCount,
    addNotification,
    clearNotification,
    removeNotification,
    setNotificationTo
  };
};

export { useBrowserTabNotification };
